/**
 * imageApi — image upload/view flow (Option A: presign → PUT → key).
 *
 * The presign/commit/view calls delegate to the GENERATED `ImageControllerApi` (per AGENTS.md §3:
 * the frontend never hand-writes API calls). The only hand-written piece is `uploadToS3`, which
 * PUTs bytes DIRECTLY to S3 using the presigned URL — that request never goes through our backend
 * or the generated client, so it has no generated counterpart.
 *
 * Flow:
 *   1. presignUpload(contentType, purpose) → { objectKey, uploadUrl }   (backend mints the key)
 *   2. uploadToS3(uploadUrl, file)         → browser PUTs bytes straight to S3
 *   3. caller persists `objectKey` on the entity (Area.imageUrl, MachinePartType.imageUrl, …)
 *   4. presignView(objectKey)              → short-lived GET url for display
 */

import { ImageControllerApi } from '../api-generated/apis';
import { apiConfig } from '../api-generated/apiConfig';

/** Logical grouping used by the backend to choose the S3 key prefix. */
export type ImagePurpose = 'area' | 'part-type' | 'result';

const imageControllerApi = new ImageControllerApi(apiConfig);

/** Ask the backend for a presigned PUT url and the object key it minted. */
export async function presignUpload(
  contentType: string,
  purpose: ImagePurpose,
): Promise<{ objectKey: string; uploadUrl: string }> {
  const res = await imageControllerApi.presignUpload({
    presignUploadRequest: { contentType, purpose },
  });
  return { objectKey: res.objectKey ?? '', uploadUrl: res.uploadUrl ?? '' };
}

/** Resolve a stored object key to a short-lived GET url for rendering. */
export async function presignView(objectKey: string): Promise<{ url: string }> {
  const res = await imageControllerApi.presignView({ key: objectKey });
  return { url: res.url ?? '' };
}

/** Promote a pending upload to its permanent prefix (optional; entity save can also just keep the key). */
export async function commitImage(
  objectKey: string,
  purpose: ImagePurpose,
): Promise<{ objectKey: string }> {
  const res = await imageControllerApi.commit({
    commitImageRequest: { objectKey, purpose },
  });
  return { objectKey: res.objectKey ?? objectKey };
}

/**
 * Upload the raw file bytes straight to S3 using the presigned PUT url.
 * `onProgress` reports 0..100 when supported by the browser.
 *
 * This is intentionally NOT part of the generated client: it targets S3 directly, not our API.
 */
export function uploadToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    // The presigned url is signed for this exact content type; it must match.
    xhr.setRequestHeader('Content-Type', file.type);

    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('S3 upload failed: network error'));
    xhr.send(file);
  });
}
